import { Modal } from "@liberetech/design-system";
import ActionSheet from "@liberetech/design-system/ActionSheet";
import Button from "@liberetech/design-system/Button";
import Link from "@liberetech/design-system/Link";
import NavLink from "@liberetech/design-system/NavLink";
import { useState } from "react";

import { useTrackingContext } from "components/Context/TrackingContext";
import ListItem from "components/ListItem/ListItem";
import ListItemCheckbox from "components/ListItem/ListItemCheckbox";
import { Trans, useTranslation } from "lib/i18n/client";
import { allCookieGroupsV2, TrackingCookieGroupV2 } from "lib/tracking";
import useForm from "lib/useForm";

import styles from "./LayoutCookieBanner.module.css";

const LayoutCookieBanner: React.FC<LayoutCookieBannerType> = ({ onClose }) => {
  const { t } = useTranslation("common");
  const { allowTracking } = useTrackingContext();
  const [isPreferencesOpen, setPreferencesOpen] = useState<boolean>(false);
  const { register, handleSubmit } = useForm<{
    [Key in TrackingCookieGroupV2]: boolean;
  }>({
    translationContext: "common",
  });
  const acceptAll = () => {
    allowTracking(allCookieGroupsV2);
    onClose();
  };
  const rejectAll = () => {
    allowTracking([]);
    onClose();
  };
  return (
    <>
      <Modal
        title={t("cookieBanner.preferences.title")}
        actions={
          <div className={styles.buttons}>
            <Button className={styles.button} onClick={acceptAll}>
              {t("cookieBanner.accept")}
            </Button>
            <Button type="button" onClick={rejectAll}>
              {t("cookieBanner.reject")}
            </Button>
            <button onClick={() => setPreferencesOpen(true)}>
              <Link>{t("cookieBanner.preferences.title")}</Link>
            </button>
          </div>
        }
      >
        <Trans
          i18nKey={"common:cookieBanner.text"}
          components={{
            cookiesLink: <LayoutCookieBannerPolicyLink />,
          }}
        />
      </Modal>
      <ActionSheet
        className={styles.preferencesModal}
        isOpen={isPreferencesOpen}
        title={t("cookieBanner.preferences.title")}
        actions={
          <>
            <Button type="button" onClick={acceptAll}>
              {t("cookieBanner.accept")}
            </Button>
            <Button secondary form="layout-cookie-banner-preferences">
              {t("cookieBanner.preferences.save")}
            </Button>
          </>
        }
        onRequestClose={() => {
          setPreferencesOpen(false);
        }}
      >
        <form
          id="layout-cookie-banner-preferences"
          onSubmit={handleSubmit((data) => {
            if (data.ad_storage) {
              data.ad_user_data = true;
            }
            allowTracking(
              (Object.keys(data) as Array<TrackingCookieGroupV2>).reduce(
                (prev, group: TrackingCookieGroupV2) => {
                  return data[group] === true ? [...prev, group] : prev;
                },
                [] as TrackingCookieGroupV2[],
              ),
            );
            onClose();
          })}
        >
          <p>{t("cookieBanner.preferences.description")}</p>
          <ul className={styles.preferences}>
            <ListItem
              title={t("cookieBanner.preferences.type.technical")}
              endElement={
                <span className={styles.preferenceEnabled}>
                  {t("cookieBanner.preferences.enabled")}
                </span>
              }
              disabled
            />
            <ListItemCheckbox
              inputProps={register("analytics_storage")}
              title={t("cookieBanner.preferences.type.analytics")}
            />

            <ListItemCheckbox
              inputProps={register("ad_storage")}
              title={t("cookieBanner.preferences.type.ad")}
            />

            <ListItemCheckbox
              inputProps={register("ad_personalization")}
              title={t("cookieBanner.preferences.type.personalization")}
            />
          </ul>
        </form>
      </ActionSheet>
    </>
  );
};

const LayoutCookieBannerPolicyLink: React.FC = ({ children }) => {
  const { t } = useTranslation("common");
  return (
    <NavLink href={t("cookieBanner.policy.url")}>
      <Link>{children}</Link>
    </NavLink>
  );
};

type LayoutCookieBannerType = {
  onClose: () => void;
};

export default LayoutCookieBanner;
